import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

const THEME = {
  accent: "#eefa94",
  surface: "#e9eeea",
  triggerOpen: "border-gray-400 ring-2 ring-gray-200",
  triggerIdle: "border-gray-300 hover:border-gray-400",
};

function ThemeCheckbox({ checked }) {
  return (
    <span
      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
        checked
          ? "bg-gray-900 border-gray-900"
          : "bg-white border-gray-300"
      }`}
    >
      {checked && <Check size={11} className="text-white" strokeWidth={3} />}
    </span>
  );
}

/**
 * Multi-select checkbox dropdown filter — matches Templates page theme.
 */
export default function TemplateCheckboxFilter({
  label,
  options = [],
  selectedIds = [],
  onApply,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(selectedIds);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPending(selectedIds);
    }
  }, [selectedIds, open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const allIds = options.map((o) => o.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => pending.includes(id));

  const buttonLabel = () => {
    if (!selectedIds.length) return label;
    if (selectedIds.length === 1) {
      const opt = options.find((o) => o.id === selectedIds[0]);
      return opt?.label || label;
    }
    return `${selectedIds.length} options selected`;
  };

  const toggleId = (id) => {
    setPending((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    setPending(allSelected ? [] : [...allIds]);
  };

  const handleApply = () => {
    onApply(pending);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-medium text-gray-900 border rounded-xl bg-white min-w-[140px] max-w-[220px] transition-all shadow-sm ${
          open ? THEME.triggerOpen : THEME.triggerIdle
        }`}
      >
        <span className="truncate text-left">{buttonLabel()}</span>
        <ChevronDown
          size={16}
          className={`text-gray-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 z-50 mt-2 min-w-[260px] max-w-[320px] bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden"
        >
          <div className="max-h-[320px] overflow-y-auto py-1">
            {options.length > 1 && (
              <button
                type="button"
                onClick={toggleAll}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-900 hover:bg-[#e9eeea] text-left transition-colors"
              >
                <ThemeCheckbox checked={allSelected} />
                <span className="font-medium">Select all</span>
              </button>
            )}
            {options.map((opt) => {
              const checked = pending.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleId(opt.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-[#e9eeea]"
                  style={
                    checked ? { backgroundColor: "rgba(238, 250, 148, 0.35)" } : undefined
                  }
                >
                  <ThemeCheckbox checked={checked} />
                  <span className="text-gray-800">{opt.label}</span>
                </button>
              );
            })}
            {options.length === 0 && (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">
                No options available
              </p>
            )}
          </div>
          <div
            className="border-t border-gray-200 px-4 py-3 flex justify-end"
            style={{ backgroundColor: THEME.surface }}
          >
            <button
              type="button"
              onClick={handleApply}
              className="px-5 py-2 text-sm font-semibold text-gray-900 rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all"
              style={{ backgroundColor: THEME.accent }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
